import { useState, useEffect, useMemo } from 'react';
import { LS } from '../../utils/storage';
import { IRON_RANKS } from '../../data/ranks';
import { today, ago, calcStreak } from '../../utils/helpers';
import { V, Haptic } from '../../utils/theme';
import { appName } from '../../utils/brand';
import { Btn, SuccessToastCtrl, MsgBannerCtrl } from '../../components/ui';
import { calcEarnedBadges } from '../../data/badges';
import { ShareCard } from '../../utils/share';

export function getXPBonus(){return LS.get("ft-xp-bonus")||{total:0,log:[]};}
export function addXPBonus(amount,reason){
  const b=getXPBonus();
  const entry={date:today(),amount,reason};
  const updated={total:(b.total||0)+amount,log:[entry,...(b.log||[])].slice(0,100)};
  LS.set("ft-xp-bonus",updated);
  return updated.total;
}

// ── Streak Shields ──
export function getShields(){return Math.min(3,LS.get("ft-streak-shields")||0);}
export function addShield(){
  const n=Math.min(3,getShields()+1);
  LS.set("ft-streak-shields",n);
  return n;
}
export function consumeShield(){
  const n=Math.max(0,getShields()-1);
  LS.set("ft-streak-shields",n);
  LS.set("ft-shield-used-date",today());
  return n;
}
// Check + award shield when 7-day milestone hit today
export function checkStreakShieldAward(streak){
  if(streak>0&&streak%7===0){
    const lastAward=LS.get("ft-shield-awarded-streak");
    if(lastAward!==streak){
      LS.set("ft-shield-awarded-streak",streak);
      const n=addShield();
      SuccessToastCtrl.show(`🛡️ Streak Shield earned! You have ${n} shield${n!==1?"s":""}`);
      return true;
    }
  }
  return false;
}
// On app load / tab change: check if streak should break but shield covers it
export function checkStreakShieldActivation(s){
  const shields=getShields();
  if(shields<=0)return false;
  const dates=new Set((s.workouts||[]).map(w=>w.date));
  if(dates.size===0)return false;
  const shieldUsed=LS.get("ft-shield-used-date");
  // calcStreak already forgives "not trained yet today", so a shield is only needed once
  // yesterday was missed entirely (last session two days ago). The old elapsed-hours check
  // fired every afternoon after a normal training day and burned a shield a day.
  if(dates.has(today())||dates.has(ago(1))||!dates.has(ago(2))||shieldUsed===today())return false;
  // Streak as of the last training day (the days before the gap)
  const preGapStreak=(()=>{let c=0,i=2;while(dates.has(ago(i))){c++;i++;}return c;})();
  // Record the bridged day so calcStreak treats it as trained
  const covered=LS.get("ft-shield-covered-dates")||[];
  if(!covered.includes(ago(1)))LS.set("ft-shield-covered-dates",[...covered,ago(1)].slice(-30));
  consumeShield();
  MsgBannerCtrl.push({name:"🛡️ Shield Activated",
    text:`Your ${preGapStreak}-day streak is protected! Train today to keep it.`,type:"system"});
  return true;
}

// ── XP Multipliers ──
export function getActiveMultiplier(s){
  const dow=new Date().getDay(); // 0=Sun,2=Tue,4=Thu
  const lastW=s.workouts?.[0]?.date;
  const daysSinceWorkout=lastW?Math.floor((new Date()-new Date(lastW+"T12:00:00"))/86400000):99;
  // Comeback bonus (5+ days off)
  if(daysSinceWorkout>=5){
    const usedDate=LS.get("ft-comeback-used");
    if(usedDate!==today())return{mult:3,label:"3× Comeback Bonus",color:"#22c55e",icon:"💥"};
  }
  // Double XP Tuesday & Thursday
  if(dow===2||dow===4){
    return{mult:2,label:"2× Double XP Day",color:"#f59e0b",icon:"⚡"};
  }
  // 3-day combo (3 workouts in last 3 calendar days) — only if no gap >= 2 days
  const last3=[today(),ago(1),ago(2)];
  const comboHit=last3.every(d=>(s.workouts||[]).some(w=>w.date===d));
  if(comboHit&&daysSinceWorkout<2)return{mult:1.5,label:"1.5× 3-Day Combo",color:"#a78bfa",icon:"🔥"};
  return{mult:1,label:null,color:null,icon:null};
}

// ── Core calcIronScore (base XP from history + bonus pool) ──
export function calcIronScore(s){
  let baseXP=0;
  const streak=calcStreak(s.workouts);
  baseXP += (s.workouts||[]).length * 30;
  baseXP += streak * 8;
  const getBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{const w2=parseFloat(st.weight)||0;if(w2>b)b=w2;});}));return b;};
  const big3=getBest("bench")+getBest("squat")+getBest("deadlift");
  baseXP += Math.floor(big3/8);
  baseXP += (s.nutrition||[]).length*8;
  baseXP += (s.photos||[]).length*15;
  baseXP += (s.checkins||[]).length*5;
  const {checks}=calcEarnedBadges(s);
  baseXP += Object.values(checks).filter(Boolean).length*75;
  // Streak milestones
  if(streak>=7)baseXP+=150;if(streak>=14)baseXP+=300;if(streak>=30)baseXP+=750;if(streak>=60)baseXP+=1500;if(streak>=100)baseXP+=3000;
  const nutStreak2=(()=>{let c=0;const b2=(s.nutrition||[]).some(n=>n.date===today())?0:1;for(let i=b2;i<365;i++){if((s.nutrition||[]).some(n=>n.date===ago(i)))c++;else break;}return c;})();
  if(nutStreak2>=7)baseXP+=100;if(nutStreak2>=14)baseXP+=200;if(nutStreak2>=30)baseXP+=500;
  // Strength milestones
  if(big3>=500)baseXP+=200;if(big3>=800)baseXP+=400;if(big3>=1000)baseXP+=600;if(big3>=1200)baseXP+=1000;if(big3>=1500)baseXP+=2000;
  // Consistency bonuses
  const monthWorkouts=(s.workouts||[]).filter(w=>w.date>=ago(30)).length;
  if(monthWorkouts>=16)baseXP+=500;if(monthWorkouts>=20)baseXP+=1000;
  // Add bonus pool (missions, multipliers, events)
  const bonusXP=getXPBonus().total||0;
  const xp=baseXP+bonusXP;
  // Save for snapshot
  LS.set("ft-ironscore",xp);
  const rankData=[...IRON_RANKS].reverse().find(r=>xp>=r.xpNeeded)||IRON_RANKS[0];
  const nextRank=IRON_RANKS.find(r=>r.level===rankData.level+1);
  const xpIntoRank=xp-rankData.xpNeeded;
  const xpForNext=nextRank?(nextRank.xpNeeded-rankData.xpNeeded):null;
  const pct=xpForNext?Math.min(100,Math.round(xpIntoRank/xpForNext*100)):100;
  return{xp,baseXP,bonusXP,rank:rankData,nextRank,xpIntoRank,xpForNext,pct,streak,big3};
}

// ── Level-Up Detection ──

export function useLevelUp(s){
  const {rank}=useMemo(()=>calcIronScore(s),[s.workouts,s.nutrition,s.photos,s.checkins,s.body,s._xpTs]);
  const [celebrating,setCelebrating]=useState(false);
  const [celebRank,setCelebRank]=useState(null);
  useEffect(()=>{
    const savedLevel=LS.get("ft-last-known-level")||1;
    if(rank.level>savedLevel){
      LS.set("ft-last-known-level",rank.level);
      setCelebRank(rank);
      setTimeout(()=>setCelebrating(true),400);
    } else {
      // Ensure level is always persisted on first render
      if(!LS.get("ft-last-known-level")) LS.set("ft-last-known-level",rank.level);
    }
  },[rank.level]);
  return{celebrating,celebRank,dismiss:()=>setCelebrating(false)};
}

// ── Level-Up Celebration Overlay ──
export function LevelUpCelebration({rank,onDismiss,s}){
  const [show,setShow]=useState(false);
  const [phase,setPhase]=useState(0); // 0=burst 1=card
  useEffect(()=>{setTimeout(()=>{setShow(true);setTimeout(()=>setPhase(1),600)},50);},[]);
  const dismiss=()=>{setShow(false);setTimeout(onDismiss,300);};
  const {xp,rank:newRank,streak}=calcIronScore(s);
  const prevRank=IRON_RANKS.find(r=>r.level===rank.level-1);
  // Stats to show in celebration
  const totalW=(s.workouts||[]).length;
  const shields=getShields();
  // Share card via canvas
  const shareRankCard=async()=>{
    try{
      const stats=[
        {value:rank.icon+" "+rank.name,label:"NEW RANK",color:rank.color},
        {value:xp.toLocaleString(),label:"IRON XP",color:"#e8e8ec"},
        {value:"Lv "+rank.level+"/30",label:"LEVEL",color:rank.tierColor},
        {value:totalW+" workouts",label:"ALL TIME",color:"#94a3b8"},
      ];
      const c2=await ShareCard.generate(rank.icon+" RANK UP!",stats,`${rank.name} · Level ${rank.level} · ${xp.toLocaleString()} XP`);
      ShareCard.share(c2,`ironlog-rankup-${today()}.png`);
    }catch(e){
      if(navigator.share)navigator.share({title:`${appName()} Rank Up!`,text:`Just hit ${rank.name} on ${appName()} 💪 ${xp.toLocaleString()} XP`}).catch(()=>{});
    }
  };
  return(
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",overflowY:"auto",
      background:`radial-gradient(ellipse at 50% 40%,${rank.color}22 0%,rgba(0,0,0,0.94) 65%)`,
      opacity:show?1:0,transition:"opacity .3s"}}>
      {/* ── T4 #7: Enhanced burst particles (40 vs 20) ── */}
      <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
        {Array.from({length:40}).map((_,i)=>{
          const x=5+Math.random()*90;
          const sz=4+Math.random()*8;
          const delay=Math.random()*2;
          const dur=1.2+Math.random()*1.5;
          const colors=[rank.color,rank.tierColor,"#fff","#fbbf24","#f43f5e",V.accent];
          return <div key={i} style={{position:"absolute",
            left:`${x}%`,top:"-12px",
            width:sz,height:sz,borderRadius:i%4===0?"2px":"50%",
            background:colors[i%colors.length],
            animation:`fall ${dur}s ${delay}s ease-in forwards`,
            opacity:0.9}}/>;
        })}
      </div>
      <style>{`
        @keyframes fall{0%{transform:translateY(0) rotate(0deg) scale(1);opacity:1}100%{transform:translateY(110vh) rotate(${720+Math.round(Math.random()*360)}deg) scale(0.3);opacity:0}}
        @keyframes rankPop{0%{transform:scale(0.5) rotate(-8deg);opacity:0}60%{transform:scale(1.08) rotate(2deg);opacity:1}100%{transform:scale(1) rotate(0deg);opacity:1}}
        @keyframes rankShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes rankGlow{0%,100%{box-shadow:0 0 20px ${rank.color}40}50%{box-shadow:0 0 50px ${rank.color}80,0 0 80px ${rank.color}30}}
      `}</style>

      {/* ── Rank-up main card ── */}
      <div style={{position:"relative",zIndex:1,width:"min(340px,92vw)",margin:"auto",
        padding:"28px 24px 24px",textAlign:"center",
        background:`linear-gradient(160deg,${rank.color}20,rgba(10,10,15,0.95) 60%)`,
        border:`2px solid ${rank.color}70`,borderRadius:28,
        animation:phase>=1?"rankPop .5s cubic-bezier(.17,.67,.35,1.3) forwards":"none",
        opacity:phase>=1?undefined:0}}>

        {/* Tier badge */}
        <div style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",
          borderRadius:20,background:`${rank.tierColor}18`,border:`1px solid ${rank.tierColor}35`,
          marginBottom:14}}>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:2,color:rank.tierColor,textTransform:"uppercase"}}>
            {rank.tier} TIER
          </span>
        </div>

        {/* Big icon with glow ring */}
        <div style={{position:"relative",display:"inline-block",marginBottom:12}}>
          <div style={{width:110,height:110,borderRadius:"50%",margin:"0 auto",
            background:`radial-gradient(circle,${rank.color}25,${rank.color}08)`,
            border:`3px solid ${rank.color}60`,
            display:"flex",alignItems:"center",justifyContent:"center",
            animation:"rankGlow 2s ease-in-out infinite",
            fontSize:58,lineHeight:1}}>{rank.icon}</div>
          {prevRank&&(
            <div style={{position:"absolute",bottom:-6,right:-6,
              width:28,height:28,borderRadius:"50%",
              background:V.bg,border:`2px solid ${rank.color}50`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:14}}>{prevRank.icon}</div>
          )}
        </div>

        <div style={{fontSize:11,fontWeight:700,letterSpacing:3,color:rank.color,
          textTransform:"uppercase",opacity:0.8,marginBottom:6}}>RANK UP!</div>
        <div style={{fontSize:30,fontWeight:900,color:rank.color,lineHeight:1.05,marginBottom:4}}>
          {rank.name}
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:16,fontFamily:"monospace"}}>
          Level {rank.level} of 30 · {xp.toLocaleString()} XP
        </div>

        {/* Shimmer bar */}
        <div style={{height:5,borderRadius:3,background:V.hoverBg,marginBottom:16,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:3,
            background:`linear-gradient(90deg,${rank.color}00,${rank.color},${rank.tierColor},${rank.color}00)`,
            backgroundSize:"200% 100%",animation:"rankShimmer 1.8s ease-in-out infinite"}}/>
        </div>

        {/* Stats row */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:18}}>
          {[
            {icon:"💪",val:totalW,label:"workouts"},
            {icon:"🔥",val:streak+"d",label:"streak"},
            {icon:"🛡️",val:shields,label:"shields"},
          ].map(stat=>(
            <div key={stat.label} style={{padding:"8px 6px",borderRadius:10,
              background:V.inputBg,border:`1px solid ${V.cardBorder}`}}>
              <div style={{fontSize:11,marginBottom:2}}>{stat.icon}</div>
              <div style={{fontSize:15,fontWeight:800,color:V.text,fontFamily:V.mono,lineHeight:1}}>{stat.val}</div>
              <div style={{fontSize:11,color:V.text3,marginTop:2}}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{display:"flex",gap:8}}>
          <button onClick={shareRankCard}
            style={{flex:1,padding:"11px",borderRadius:12,
              background:`${rank.color}15`,border:`1.5px solid ${rank.color}40`,
              cursor:"pointer",fontSize:11,fontWeight:700,color:rank.color,fontFamily:V.font,
              display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
            📤 Share
          </button>
          <button onClick={dismiss}
            style={{flex:2,padding:"11px",borderRadius:12,
              background:`linear-gradient(135deg,${rank.color},${rank.tierColor})`,
              border:"none",cursor:"pointer",
              fontSize:13,fontWeight:900,color:V.bg,fontFamily:V.font,
              boxShadow:`0 4px 20px ${rank.color}50`}}>
            Let's Go! 💪
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// IRONLOG HELP SYSTEM
// HelpBtn — the ? button used throughout the app
// HelpModal — full-screen topic sheet
// HELP_TOPICS — all tutorial content in one place
// ═══════════════════════════════════════════════════════


